import { useCallback, useEffect, useRef, useState } from 'react';
import { isRight } from 'fp-ts/lib/Either';
import di from '../../../../../../../../di/DependencyInjection';
import GetAllRolesUseCase, { GetAllRolesUseCaseName } from '../../../../../../../../domain/use_cases/roles/GetAllRolesUseCase';
import SearchUserPositionsUseCase, { SearchUserPositionsUseCaseName } from '../../../../../../../../domain/use_cases/user/SearchUserPositionsUseCase';
import SearchUserBUnitsUseCase, { SearchUserBUnitsUseCaseName } from '../../../../../../../../domain/use_cases/user/SearchUserBUnitsUseCase';
import SearchAlSupervisorsUseCase, { SearchAlSupervisorsUseCaseName } from '../../../../../../../../domain/use_cases/user/SearchAlSupervisorsUseCase';
import Constants, { ConstantsName } from '../../../../../../../../constants/Constants';
import { Auth } from 'aws-amplify';
import { DataLoadingState, WizardLoadedData } from '../types';
import { BucketNode } from '../../BucketTreePicker';
import RoleEntity from '../../../../../../../../domain/entities/RoleEntity';

const TIMEOUT_MS = 30_000;

/** Hardcoded fallback timezones when GET /config fails. */
const defaultTimeZones: Array<{ label: string; subLabel: string }> = [
    { label: "America/Belize", subLabel: "" },
    { label: "America/Bogota", subLabel: "" },
    { label: "America/Jamaica", subLabel: "" },
    { label: "America/Tegucigalpa", subLabel: "" },
    { label: "America/Guyana", subLabel: "" },
    { label: "America/St_Lucia", subLabel: "" },
    { label: "US/Eastern", subLabel: "" },
    { label: "US/Central", subLabel: "" },
    { label: "US/Mountain", subLabel: "" },
    { label: "US/Pacific", subLabel: "" },
    { label: "US/Alaska", subLabel: "" },
];

/** Hardcoded fallback locations when GET /config fails. */
const defaultLocationsOptions: Array<{ label: string; subLabel: string }> = [
    { label: "Belize", subLabel: "" },
    { label: "Jamaica", subLabel: "" },
    { label: "Colombia", subLabel: "" },
    { label: "Guyana", subLabel: "" },
    { label: "Saint Lucia", subLabel: "" },
    { label: "US", subLabel: "" },
    { label: "Honduras", subLabel: "" },
    { label: "Training", subLabel: "" },
];

type SourceName = 'roles' | 'positions' | 'businessUnits' | 'supervisors' | 'config' | 'bucketTree';

const CRITICAL_SOURCES: SourceName[] = ['roles', 'positions', 'businessUnits', 'supervisors'];

/**
 * Wraps a promise with a timeout. Rejects if the promise does not settle within `ms` milliseconds.
 */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('Timeout')), ms);
        promise.then(
            (value) => { clearTimeout(timer); resolve(value); },
            (err) => { clearTimeout(timer); reject(err); },
        );
    });
}

interface SourceResults {
    roles?: RoleEntity[];
    positions?: Array<{ name: string; [key: string]: any }>;
    businessUnits?: Array<{ name: string; [key: string]: any }>;
    supervisors?: Array<{ label: string; subLabel: string }>;
    timezones?: Array<{ label: string; subLabel: string }>;
    locations?: Array<{ label: string; subLabel: string }>;
    clients?: Array<{ label: string; subLabel: string }>;
    bucketTree?: BucketNode[];
    bucketTreeError?: string | null;
}

/**
 * Custom hook that loads all wizard reference data in parallel on mount.
 *
 * - Critical sources (roles, positions, business units, supervisors): if ANY fails → error state.
 * - Non-critical GET /config: falls back to hardcoded defaults.
 * - Non-critical GET /buckets/tree: hides tree picker on failure.
 * - Provides a `retry()` function that re-fetches only previously failed sources.
 * - Applies a 30-second timeout per source.
 * - Sorts roles alphabetically by name.
 */
export function useWizardDataLoader(): { state: DataLoadingState; retry: () => void } {
    const [state, setState] = useState<DataLoadingState>({ status: 'loading' });
    const failedSourcesRef = useRef<SourceName[]>([]);
    const partialDataRef = useRef<SourceResults>({});
    const mountedRef = useRef(true);

    const fetchRoles = useCallback(async (): Promise<RoleEntity[]> => {
        const result = await withTimeout(
            di.get<GetAllRolesUseCase>(GetAllRolesUseCaseName).call(true),
            TIMEOUT_MS,
        );
        if (isRight(result)) {
            const sorted = [...result.right].sort((a, b) => a.name.localeCompare(b.name));
            return sorted;
        }
        throw new Error('Failed to load roles');
    }, []);

    const fetchPositions = useCallback(async (): Promise<Array<{ name: string; [key: string]: any }>> => {
        const result = await withTimeout(
            di.get<SearchUserPositionsUseCase>(SearchUserPositionsUseCaseName).call(),
            TIMEOUT_MS,
        );
        if (isRight(result)) {
            return result.right as any[];
        }
        throw new Error('Failed to load positions');
    }, []);

    const fetchBusinessUnits = useCallback(async (): Promise<Array<{ name: string; [key: string]: any }>> => {
        const result = await withTimeout(
            di.get<SearchUserBUnitsUseCase>(SearchUserBUnitsUseCaseName).call(),
            TIMEOUT_MS,
        );
        if (isRight(result)) {
            return result.right as any[];
        }
        throw new Error('Failed to load business units');
    }, []);

    const fetchSupervisors = useCallback(async (): Promise<Array<{ label: string; subLabel: string }>> => {
        const result = await withTimeout(
            di.get<SearchAlSupervisorsUseCase>(SearchAlSupervisorsUseCaseName).call(),
            TIMEOUT_MS,
        );
        if (isRight(result)) {
            return result.right.map((sup: any) => ({
                label: `${sup.firstname} ${sup.lastname}`,
                subLabel: sup.email,
            }));
        }
        throw new Error('Failed to load supervisors');
    }, []);

    const fetchConfig = useCallback(async (): Promise<{
        timezones: Array<{ label: string; subLabel: string }>;
        locations: Array<{ label: string; subLabel: string }>;
        clients: Array<{ label: string; subLabel: string }>;
    }> => {
        const baseUrl = di.get<Constants>(ConstantsName).HOST_API_USERS_URL;
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

        try {
            const response = await fetch(`${baseUrl}/config`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'Authorization': token },
                signal: controller.signal,
            });
            clearTimeout(timeoutId);

            if (!response.ok) throw new Error(`Config fetch failed: ${response.status}`);

            const data = await response.json();
            return {
                timezones: Array.isArray(data.timezone)
                    ? data.timezone.map((tz: string) => ({ label: tz, subLabel: "" }))
                    : defaultTimeZones,
                locations: Array.isArray(data.location)
                    ? data.location.map((loc: string) => ({ label: loc, subLabel: "" }))
                    : defaultLocationsOptions,
                clients: Array.isArray(data.client)
                    ? data.client.map((cl: string) => ({ label: cl, subLabel: "" }))
                    : [],
            };
        } catch {
            clearTimeout(timeoutId);
            throw new Error('Failed to load config');
        }
    }, []);

    const fetchBucketTree = useCallback(async (): Promise<BucketNode[]> => {
        const baseUrl = di.get<Constants>(ConstantsName).HOST_API_USERS_URL;
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

        try {
            const response = await fetch(`${baseUrl}/buckets/tree`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'Authorization': token },
                signal: controller.signal,
            });
            clearTimeout(timeoutId);

            if (!response.ok) throw new Error(`Bucket tree fetch failed: ${response.status}`);

            const data = await response.json();
            return data || [];
        } catch {
            clearTimeout(timeoutId);
            throw new Error('Failed to load bucket tree');
        }
    }, []);

    const loadSources = useCallback(async (sourcesToLoad?: SourceName[]) => {
        if (!mountedRef.current) return;
        setState({ status: 'loading' });

        const targets = sourcesToLoad ?? (['roles', 'positions', 'businessUnits', 'supervisors', 'config', 'bucketTree'] as SourceName[]);
        const newFailed: SourceName[] = [];
        const results: SourceResults = { ...partialDataRef.current };

        // Build fetch promises for requested sources
        const promises: Array<{ source: SourceName; promise: Promise<void> }> = [];

        if (targets.includes('roles')) {
            promises.push({
                source: 'roles',
                promise: fetchRoles()
                    .then((data) => { results.roles = data; })
                    .catch(() => { newFailed.push('roles'); }),
            });
        }

        if (targets.includes('positions')) {
            promises.push({
                source: 'positions',
                promise: fetchPositions()
                    .then((data) => { results.positions = data; })
                    .catch(() => { newFailed.push('positions'); }),
            });
        }

        if (targets.includes('businessUnits')) {
            promises.push({
                source: 'businessUnits',
                promise: fetchBusinessUnits()
                    .then((data) => { results.businessUnits = data; })
                    .catch(() => { newFailed.push('businessUnits'); }),
            });
        }

        if (targets.includes('supervisors')) {
            promises.push({
                source: 'supervisors',
                promise: fetchSupervisors()
                    .then((data) => { results.supervisors = data; })
                    .catch(() => { newFailed.push('supervisors'); }),
            });
        }

        if (targets.includes('config')) {
            promises.push({
                source: 'config',
                promise: fetchConfig()
                    .then((data) => {
                        results.timezones = data.timezones;
                        results.locations = data.locations;
                        results.clients = data.clients;
                    })
                    .catch(() => {
                        // Non-critical: fall back to defaults
                        results.timezones = defaultTimeZones;
                        results.locations = defaultLocationsOptions;
                        results.clients = [];
                    }),
            });
        }

        if (targets.includes('bucketTree')) {
            promises.push({
                source: 'bucketTree',
                promise: fetchBucketTree()
                    .then((data) => {
                        results.bucketTree = data;
                        results.bucketTreeError = null;
                    })
                    .catch(() => {
                        // Non-critical: hide tree picker
                        results.bucketTree = [];
                        results.bucketTreeError = 'Failed to load bucket hierarchy';
                    }),
            });
        }

        // Execute all in parallel
        await Promise.all(promises.map((p) => p.promise));

        if (!mountedRef.current) return;

        // Check for critical failures
        const criticalFailures = newFailed.filter((s) => CRITICAL_SOURCES.includes(s));

        partialDataRef.current = results;
        failedSourcesRef.current = newFailed;

        if (criticalFailures.length > 0) {
            setState({
                status: 'error',
                failedSources: criticalFailures,
                partialData: buildPartialData(results),
            });
        } else {
            setState({
                status: 'ready',
                data: buildLoadedData(results),
            });
        }
    }, [fetchRoles, fetchPositions, fetchBusinessUnits, fetchSupervisors, fetchConfig, fetchBucketTree]);

    const retry = useCallback(() => {
        const sourcesToRetry = failedSourcesRef.current.length > 0
            ? failedSourcesRef.current
            : undefined;
        loadSources(sourcesToRetry);
    }, [loadSources]);

    useEffect(() => {
        mountedRef.current = true;
        loadSources();
        return () => { mountedRef.current = false; };
    }, [loadSources]);

    return { state, retry };
}

function buildLoadedData(results: SourceResults): WizardLoadedData {
    return {
        roles: results.roles ?? [],
        positions: results.positions ?? [],
        businessUnits: results.businessUnits ?? [],
        supervisors: results.supervisors ?? [],
        timezones: results.timezones ?? defaultTimeZones,
        locations: results.locations ?? defaultLocationsOptions,
        clients: results.clients ?? [],
        bucketTree: results.bucketTree ?? [],
        bucketTreeLoading: false,
        bucketTreeError: results.bucketTreeError ?? null,
    };
}

function buildPartialData(results: SourceResults): Partial<WizardLoadedData> {
    const partial: Partial<WizardLoadedData> = {};
    if (results.roles) partial.roles = results.roles;
    if (results.positions) partial.positions = results.positions;
    if (results.businessUnits) partial.businessUnits = results.businessUnits;
    if (results.supervisors) partial.supervisors = results.supervisors;
    if (results.timezones) partial.timezones = results.timezones;
    if (results.locations) partial.locations = results.locations;
    if (results.clients) partial.clients = results.clients;
    if (results.bucketTree) partial.bucketTree = results.bucketTree;
    if (results.bucketTreeError !== undefined) partial.bucketTreeError = results.bucketTreeError;
    return partial;
}

export default useWizardDataLoader;
